import { useState } from "react";
import { Typography } from "@mui/material";
import type { CalculatorMode, ReferenceMaterialMode } from "@verity/database/prisma";
import { CoreFileCard, CoreInfoIndicator, CoreUpload, FileCardError, RadioOption } from "../../../core";
import i18nPackage from "../../../i18n/i18n";
import { useProctoringOptionContext } from "../../context/EditQuizContext";
import { useProctoringHelpers } from "../hooks/useProctoringHelpers";
import { RadioGroupSubItem } from "../option-menus/radioGroupSubItem";
import OptionBlock from "../optionBlock";
import styles from "./index.module.css";
import {
	REFERENCE_MATERIAL_MAX_FILE_SIZE_BYTES,
	REFERENCE_MATERIAL_ACCEPTED_FILE_TYPES,
} from "../../hooks/state/useSettingsState";
import type { ReferenceMaterialType } from "../../hooks/state/useSettingsState";
import DragAndDropIcon from "../../../core/file/images/drag-and-drop.svg?react";
import InfoIcon from "../../../core/images/info.svg?react";

const REFERENCE_MATERIAL_ACCEPTED_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

const ScientificRadioOption = () => {
	return (
		<div className={styles.scientificContainer}>
			<Typography>{i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.scientific")}</Typography>
			<CoreInfoIndicator
				hint={i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.scientificTooltip")}
				size={20}
				ariaHaspopup="false"
			/>
		</div>
	);
};

function CalculatorOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { isOptionAllowed } = useProctoringHelpers();

	const { enableCalculator } = proctoringOption;
	const calculatorAllowed = isOptionAllowed("enableCalculator");

	const calcRadioOptions: RadioOption[] = [
		{
			value: "standard",
			label: i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.standard"),
			disabled: !calculatorAllowed,
		},
		{
			value: "scientific",
			label: <ScientificRadioOption />,
			disabled: !calculatorAllowed,
		},
	];

	return (
		<OptionBlock
			checked={enableCalculator !== "off"}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					enableCalculator: checked ? "standard" : "off",
					showRecordingPanel: checked ? true : proctoringOption.showRecordingPanel,
				});
			}}
			optionKey="enableCalculator"
			menuItems={
				<RadioGroupSubItem
					radioGroupDescription={i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.subtitle")}
					radioOptions={calcRadioOptions}
					onChange={(_, value) => {
						setProctoringOption({
							...proctoringOption,
							enableCalculator: value as CalculatorMode,
						});
					}}
					selectedValue={enableCalculator}
				/>
			}
		/>
	);
}

function WhiteboardOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();

	const { enableWhiteboard } = proctoringOption;

	return (
		<OptionBlock
			optionKey="enableWhiteboard"
			checked={Boolean(enableWhiteboard)}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					enableWhiteboard: checked,
					showRecordingPanel: checked ? true : proctoringOption.showRecordingPanel,
				});
			}}
		/>
	);
}

function CodeEditorOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();

	const { enableCodeEditor } = proctoringOption;

	function generateRoomId(length = 6) {
		const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
		let roomId = "";
		for (let i = 0; i < length; i++) {
			roomId += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		return roomId;
	}

	return (
		<OptionBlock
			optionKey="enableCodeEditor"
			checked={Boolean(enableCodeEditor)}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					enableCodeEditor: checked,
					codeEditorRoomId: checked ? [generateRoomId()] : null,
					showRecordingPanel: checked ? true : proctoringOption.showRecordingPanel,
				});
			}}
		/>
	);
}

function ReferenceMaterialOption() {
	const {
		proctoringOption,
		setProctoringOption,
		referenceMaterialFiles,
		onUpdateReferenceMaterialFiles,
		referenceMaterialToggleOnly,
	} = useProctoringOptionContext();
	const { referenceMaterialMode } = proctoringOption;
	const [uploadError, setUploadError] = useState<string>();

	if (!onUpdateReferenceMaterialFiles && !referenceMaterialToggleOnly) {
		return null;
	}

	const handleFileUpload = (files: File[]) => {
		if (files.length === 0) return;
		const file = files[0];
		if (!REFERENCE_MATERIAL_ACCEPTED_MIME_TYPES.includes(file.type)) {
			setUploadError(i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.invalidFileType"));
			return;
		}

		if (file.size > REFERENCE_MATERIAL_MAX_FILE_SIZE_BYTES) {
			setUploadError(i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.fileTooLarge"));
			return;
		}

		setUploadError(undefined);
		const coreFile: ReferenceMaterialType = {
			name: file.name,
			size: file.size,
			type: file.type,
			nativeFile: file,
		};
		onUpdateReferenceMaterialFiles?.([coreFile]);
	};

	const UploadContent = () => {
		if (referenceMaterialMode === "off") {
			return null;
		}

		if (referenceMaterialMode === "student") {
			return (
				<div className={styles.referenceMaterialStudentBanner}>
					<div className={styles.svgIcon}>
						<InfoIcon aria-hidden="true" />
					</div>
					<Typography className={styles.label} component="span">
						<Typography className={styles.title} component="span">
							{i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.studentBannerTitle")}
						</Typography>
						{i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.studentBannerDesc")}
					</Typography>
				</div>
			);
		}

		if (!referenceMaterialFiles || referenceMaterialFiles.length === 0) {
			return (
				<div className={styles.referenceMaterialUploadZoneContent}>
					<CoreUpload
						onUpload={handleFileUpload}
						fileTypes={REFERENCE_MATERIAL_ACCEPTED_FILE_TYPES}
						className={styles.referenceMaterialUpload}
					>
						{(open) => (
							<div className={styles.uploadContent}>
								<div className={styles.uploadIconCircle}>
									<DragAndDropIcon />
								</div>
								<div className={styles.uploadActionRow}>
									<button type="button" className={styles.chooseFileLink} onClick={open}>
										{i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.chooseFile")}
									</button>
									<span>{i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.toUpload")}</span>
								</div>
							</div>
						)}
					</CoreUpload>
					{uploadError && <FileCardError message={uploadError} className={styles.referenceMaterialUploadError} />}
				</div>
			);
		} else {
			return (
				<div className={styles.referenceMaterialFileList}>
					{referenceMaterialFiles.map((file, i) => (
						<div key={i} className={styles.referenceMaterialFileItem}>
							<CoreFileCard
								file={file}
								onRemove={() => {
									setUploadError(undefined);
									onUpdateReferenceMaterialFiles?.([]);
								}}
								showOpenInNewTab
							/>
						</div>
					))}
				</div>
			);
		}
	};

	const referenceMaterialRadioOptions: RadioOption[] = [
		{
			value: "instructor",
			label: i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.radioBox.instructorLabel"),
			disabled: false,
		},
		{
			value: "student",
			label: i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.radioBox.studentLabel"),
			disabled: false,
		},
	];

	return (
		<OptionBlock
			optionKey="referenceMaterialMode"
			checked={referenceMaterialMode !== "off"}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					enableReferenceMaterial: checked,
					referenceMaterialMode: checked ? "instructor" : "off",
				});
				if (!checked) {
					onUpdateReferenceMaterialFiles?.([]);
				}
			}}
			menuItems={
				referenceMaterialToggleOnly ? undefined : (
					<>
						<RadioGroupSubItem
							radioGroupDescription={i18nPackage.t(
								"editQuizPage.configurationStep.showToolsTSX.referenceMaterial.subtitle",
							)}
							radioOptions={referenceMaterialRadioOptions}
							onChange={(_, value) => {
								if (value === "student") {
									onUpdateReferenceMaterialFiles?.([]);
								}
								setProctoringOption({
									...proctoringOption,
									referenceMaterialMode: value as ReferenceMaterialMode,
								});
							}}
							selectedValue={referenceMaterialMode ?? "off"}
						/>
						<UploadContent />
					</>
				)
			}
		></OptionBlock>
	);
}

export default function ShowToolsOptions() {
	const { shouldShowOption } = useProctoringHelpers();
	return (
		<>
			{shouldShowOption("enableCalculator") && <CalculatorOption />}
			{shouldShowOption("enableWhiteboard") && <WhiteboardOption />}
			{shouldShowOption("enableCodeEditor") && <CodeEditorOption />}
			{shouldShowOption("referenceMaterialMode") && <ReferenceMaterialOption />}
		</>
	);
}
